import type { AfterContentInit, OnChanges, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  HostBinding,
  Input,
  QueryList,
  inject,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import type { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { CardListItemComponent } from './card-list-item.component';
import { CardListSelectColor, CardListSize } from './card-list.types';

@Component({
  standalone: true,
  selector: 'fl-card-list',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./card-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardListComponent
  implements AfterContentInit, OnChanges, OnDestroy
{
  private timeUtils = inject(TimeUtils);

  @Input() size = CardListSize.MID;
  @Input() selectable = false;
  @Input() selectColor = CardListSelectColor.DEFAULT;

  @HostBinding('attr.role') get role(): string | undefined {
    return this.selectable ? 'listbox' : undefined;
  }
  @HostBinding('attr.aria-orientation') get orientation(): string | undefined {
    return this.selectable ? 'horizontal' : undefined;
  }

  @ContentChildren(CardListItemComponent)
  cardListItemComponents: QueryList<CardListItemComponent>;

  private childListSubscription?: Subscription;

  ngOnChanges(): void {
    // apply properties to child items whenever the properties change.
    this.applyPropertiesToChildren();
  }

  ngAfterContentInit(): void {
    // apply properties to child items whenever the list of children changes
    this.childListSubscription = this.cardListItemComponents.changes
      .pipe(startWith(this.cardListItemComponents.toArray()))
      .subscribe(() => this.applyPropertiesToChildren());
  }

  ngOnDestroy(): void {
    if (this.childListSubscription) {
      this.childListSubscription.unsubscribe();
    }
  }

  applyPropertiesToChildren(): void {
    if (this.cardListItemComponents) {
      this.cardListItemComponents.forEach((component, index, arr) => {
        component.changeDetectorRef.detach();
        component.size = this.size;
        component.color = this.selectColor;
        component.selectable = this.selectable;
        component.changeDetectorRef.reattach();

        this.timeUtils.setTimeout(() =>
          component.changeDetectorRef.markForCheck(),
        );
      });
    }
  }
}
